import { CartAddEvent } from '@theme/events';

/*
 * Shared ajax add-to-cart for the custom Jaylis sections so they all
 * route through the drawer (no full-page nav to /cart) and trigger the
 * same fly-to-cart animation as Horizon's stock product form.
 *
 * Public API:
 *   window.jaylisAjaxAdd({ body, source, imageUrl })
 *     body     – FormData (form submit) OR { id, quantity } / { items:[] }
 *     source   – the button/element the user clicked (for fly-from + UX)
 *     imageUrl – product image URL for the fly-to-cart animation
 *
 * Auto-hijacks any <form data-jaylis-ajax-add> so a form simply needs
 * that attribute (+ optional data-atc-image="<url>") to participate.
 */

function getCartItemsSectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    const id = el.getAttribute('data-section-id');
    if (id) ids.add(id);
  });
  return Array.from(ids);
}

function getCartIconElement() {
  return (
    document.querySelector('.jhd-util-cart') ||
    document.querySelector('.header-actions__cart-icon') ||
    document.querySelector('cart-icon')
  );
}

function runFlyToCart(source, imageUrl) {
  if (!source || !imageUrl) return;
  const destination = getCartIconElement();
  if (!destination) return;
  if (!customElements.get('fly-to-cart')) return;
  const fly = document.createElement('fly-to-cart');
  fly.style.setProperty('background-image', `url(${imageUrl})`);
  fly.source = source;
  fly.destination = destination;
  document.body.appendChild(fly);
}

function quantityFromBody(body) {
  if (body instanceof FormData) {
    return Number(body.get('quantity')) || 1;
  }
  if (body && Array.isArray(body.items)) {
    return body.items.reduce((sum, i) => sum + (Number(i.quantity) || 0), 0);
  }
  if (body && typeof body.quantity === 'number') {
    return body.quantity;
  }
  return 1;
}

export async function jaylisAjaxAdd({ body, source, imageUrl } = {}) {
  if (!body) throw new Error('jaylisAjaxAdd: body is required');

  const sectionsParam = getCartItemsSectionIds().join(',');

  let fetchBody;
  let headers;
  if (body instanceof FormData) {
    if (sectionsParam) body.set('sections', sectionsParam);
    fetchBody = body;
    // X-Requested-With matches Horizon's fetchConfig('javascript') and is
    // what Cloudflare bot-detection looks for to allow ajax POSTs through.
    headers = { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' };
  } else {
    const payload = { ...body };
    if (sectionsParam) payload.sections = sectionsParam;
    fetchBody = JSON.stringify(payload);
    headers = {
      'Content-Type': 'application/json',
      Accept: 'application/javascript',
      'X-Requested-With': 'XMLHttpRequest',
    };
  }

  const res = await fetch('/cart/add.js', {
    method: 'POST',
    credentials: 'same-origin',
    headers,
    body: fetchBody,
  });

  if (!res.ok) {
    let message = 'Add to cart failed';
    try {
      const err = await res.json();
      message = err.description || err.message || message;
    } catch (_) {
      // ignore
    }
    throw new Error(message);
  }

  const data = await res.json();

  runFlyToCart(source, imageUrl);

  const itemCount = quantityFromBody(body);

  document.dispatchEvent(
    new CartAddEvent({}, 'jaylis-ajax-add', {
      // Using product-form-component source so cart-icon.js increments
      // the bubble instead of replacing it – same accounting as Horizon's
      // stock add-to-cart flow.
      source: 'product-form-component',
      itemCount,
      sections: data?.sections,
    })
  );

  return data;
}

window.jaylisAjaxAdd = jaylisAjaxAdd;

document.addEventListener('submit', async (event) => {
  const target = event.target;
  const form = target instanceof Element ? target.closest('form[data-jaylis-ajax-add]') : null;
  if (!form) return;
  event.preventDefault();

  const btn = form.querySelector('button[type="submit"], button:not([type])');
  const wasDisabled = btn ? btn.disabled : false;
  if (btn) btn.disabled = true;

  try {
    const body = new FormData(form);
    const imageUrl = form.dataset.atcImage || null;
    await jaylisAjaxAdd({ body, source: btn || form, imageUrl });
  } catch (err) {
    console.error('[jaylis-ajax-add] add failed', err);
  } finally {
    if (btn) btn.disabled = wasDisabled;
  }
});
